// «Что с этим делать»: рекомендации задают общий фильтр списка блюд (как клетки матрицы и группы распределения;
// матрицу и распределение он не трогает). Новое нажатие заменяет фильтр, повторное — снимает. Десктоп — сетка 2×2
// на высоту соседней матрицы, число блюд крупно внизу карточки; телефон — чипы с «Все», пояснение строкой ниже.
// «Оставить как есть» отдельной кнопкой не показываем.
import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { CARD_LABEL_COLOR, CARD_SURFACE_SX, MUTED_TEXT_COLOR, SECTION_ACCENTS } from '../../../../styles/tokens';
import { ABC_TAGS, ALL } from '../../utils/abcCalculations';
import { tagFilter, toggleFilter } from '../../utils/abcList';
import { TAG_COLORS, plural } from './abcLook';

const ACCENT = SECTION_ACCENTS['/cost'];
const SHOWN = ['core', 'promote', 'remove', 'check'];
const focusSx = { '&.Mui-focusVisible': { outline: `2px solid ${ACCENT.accent}`, outlineOffset: '2px' } };

export default function AbcRecommendations({ tagCounts, filter, onFilter, desktop }) {
  const value = filter?.kind === 'tag' ? filter.tag : ALL;
  const toggle = (tag) => onFilter(toggleFilter(filter, tagFilter(tag)));

  if (desktop) {
    return (
      <Box component="section" aria-label="Что с этим делать" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography component="h3" sx={{ mb: 1, fontSize: '15px', fontWeight: 700, color: '#fff' }}>Что с этим делать</Typography>
        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gridTemplateRows: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
          {SHOWN.map((tag) => {
            const active = value === tag;
            const color = TAG_COLORS[tag];
            return (
              <ButtonBase
                key={tag}
                onClick={() => toggle(tag)}
                aria-pressed={active}
                disabled={!tagCounts[tag] && !active}
                sx={{
                  ...CARD_SURFACE_SX,
                  ...focusSx,
                  p: 1.75,
                  minHeight: 44,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  boxShadow: active ? `inset 0 0 0 2px ${color}` : CARD_SURFACE_SX.boxShadow,
                  opacity: tagCounts[tag] ? 1 : 0.5,
                }}
              >
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color, wordBreak: 'break-word' }}>{ABC_TAGS[tag].label}</Typography>
                <Typography sx={{ mt: 0.5, fontSize: '12px', color: CARD_LABEL_COLOR }}>{ABC_TAGS[tag].hint}</Typography>
                {/* Главное в карточке — сколько блюд: крупно внизу, как на карточках метрик */}
                <Box sx={{ mt: 'auto', pt: 1.5, display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                  <Typography sx={{ fontSize: '30px', fontWeight: 800, lineHeight: 1.1, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                    {tagCounts[tag]}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>
                    {plural(tagCounts[tag], ['блюдо', 'блюда', 'блюд'])}
                  </Typography>
                </Box>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
    );
  }

  const chips = [{ key: ALL, label: 'Все' }, ...SHOWN.map((tag) => ({ key: tag, label: ABC_TAGS[tag].label, count: tagCounts[tag] }))];
  return (
    // minWidth: 0 — ряд чипов прокручивается внутри, а не растягивает колонку сетки (и страницу) на свою ширину
    <Box component="section" aria-label="Что с этим делать" sx={{ minWidth: 0 }}>
      <Typography component="h3" sx={{ mb: 1, fontSize: '15px', fontWeight: 700, color: '#fff' }}>Что с этим делать</Typography>
      <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        {chips.map((chip) => {
          // «Все» — нет никакого фильтра (в том числе группы или клетки)
          const active = chip.key === ALL ? !filter : value === chip.key;
          const color = chip.key === ALL ? '#fff' : TAG_COLORS[chip.key];
          return (
            <ButtonBase
              key={chip.key}
              onClick={() => (chip.key === ALL ? onFilter(null) : toggle(chip.key))}
              aria-pressed={chip.key === ALL ? !filter : active}
              disabled={chip.key !== ALL && !chip.count && !active}
              sx={{
                ...focusSx,
                flexShrink: 0,
                minHeight: 44,
                px: 1.75,
                borderRadius: '22px',
                fontSize: '14px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                color: active ? '#141416' : color,
                backgroundColor: active ? color : 'rgba(255,255,255,0.06)',
                opacity: chip.key === ALL || chip.count ? 1 : 0.5,
              }}
            >
              {chip.count !== undefined ? `${chip.label} · ${chip.count}` : chip.label}
            </ButtonBase>
          );
        })}
      </Box>
      {value !== ALL && ABC_TAGS[value] && (
        <Typography sx={{ mt: 0.75, fontSize: '13px', color: MUTED_TEXT_COLOR }}>{ABC_TAGS[value].hint}</Typography>
      )}
    </Box>
  );
}
